<script setup lang="ts">
import { computed } from 'vue'

import type {
    BetaOpportunity,
    BetaOpportunityFile,
} from '@/types/beta-dashboard'

const props = defineProps<{
    opportunity: BetaOpportunity
    cdnUrl?: string
}>()

const date = computed(() => props.opportunity.created_at || 'Data não informada')

const files = (contents: string | BetaOpportunityFile[]) => (
    Array.isArray(contents) ? contents : []
)

const fileUrl = (path: string) => {
    if (/^https?:\/\//i.test(path)) return path
    return `${String(props.cdnUrl || '').replace(/\/$/, '')}/${String(path || '').replace(/^\//, '')}`
}
</script>

<template>
    <article class="beta-opportunity-card">
        <header>
            <div>
                <span>Origem</span>
                <strong>{{ opportunity.source || 'Formulário do site' }}</strong>
            </div>
            <div class="beta-opportunity-card__meta">
                <small v-if="!opportunity.read">Nova</small>
                <time>{{ date }}</time>
            </div>
        </header>

        <div class="beta-opportunity-card__fields">
            <div
                v-for="(item, index) in opportunity.contents"
                :key="`${item.title}-${index}`"
                class="beta-opportunity-card__field"
            >
                <span>{{ item.title || 'Campo sem nome' }}</span>

                <a
                    v-if="item.item_type === 'email'"
                    :href="`mailto:${item.contents}`"
                >
                    {{ item.contents }}
                </a>

                <div v-else-if="item.item_type === 'file'" class="beta-opportunity-card__files">
                    <a
                        v-for="(file, fileIndex) in files(item.contents)"
                        :key="`${file.url}-${fileIndex}`"
                        :href="fileUrl(file.url)"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Baixar arquivo {{ fileIndex + 1 }} ↗
                    </a>
                    <small v-if="!files(item.contents).length">Nenhum arquivo disponível</small>
                </div>

                <p v-else>{{ item.contents }}</p>
            </div>
        </div>
    </article>
</template>

<style scoped>
.beta-opportunity-card {
    overflow: hidden;
    border: 1px solid #e1e7ef;
    border-radius: 17px;
    background: #fff;
}

.beta-opportunity-card > header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border-bottom: 1px solid #edf1f5;
    background: #f9fbfd;
    padding: 11px 13px;
}

.beta-opportunity-card > header span,
.beta-opportunity-card > header strong {
    display: block;
}

.beta-opportunity-card > header span {
    color: #8a98aa;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.beta-opportunity-card > header strong {
    margin-top: 2px;
    color: #2d4561;
    font-size: 11px;
    font-weight: 850;
}

.beta-opportunity-card__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #7f8ea2;
    font-size: 10px;
}

.beta-opportunity-card__meta small {
    border-radius: 999px;
    background: color-mix(in srgb, var(--main-color) 9%, #edf2f7);
    padding: 4px 7px;
    color: var(--main-color);
    font-size: 10px;
    font-weight: 850;
}

.beta-opportunity-card__fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 20px;
    padding: 5px 13px 10px;
}

.beta-opportunity-card__field {
    min-width: 0;
    border-bottom: 1px solid #f0f3f7;
    padding: 8px 0;
}

.beta-opportunity-card__field > span {
    display: block;
    margin-bottom: 3px;
    color: #8290a3;
    font-size: 12px;
    font-weight: 800;
}

.beta-opportunity-card__field > p,
.beta-opportunity-card__field > a {
    overflow-wrap: anywhere;
    color: #344b67;
    font-size: 11px;
    line-height: 1.45;
    white-space: pre-wrap;
}

.beta-opportunity-card__field > a,
.beta-opportunity-card__files a {
    color: var(--main-color);
    font-weight: 750;
}

.beta-opportunity-card__files {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 10px;
}

.beta-opportunity-card__files a,
.beta-opportunity-card__files small {
    font-size: 8px;
}

.beta-opportunity-card__files small {
    color: #8b98aa;
}

@media (max-width: 600px) {
    .beta-opportunity-card__fields {
        grid-template-columns: 1fr;
    }
}
</style>
